// The chat side panel (CONTRACT §1.3, §2): the engine's wp_messages for one scope — a workpaper, the client documents or the
// engagement — threaded (questions with their answers, instructions with the agent's replies), and a composer whose mode
// decides the one inbox command it sends: Ask → wp.message (question), Note → wp.message (note), Tell agent → agent.request,
// Request from client → agent.request (the agent drafts a client request; nothing reaches the client until a person sends it).
// Answer / Accept answer / Approve plan are wp.answer / wp.accept (or the engine's own plan-approval action). The browser never
// names the author: the database stamps the signed-in person (rule 5). What was sent shows as "sending" until the engine
// publishes the message; a refusal shows the engine's sentence.
// Quick answers (owner decision 2026-09-27, Option C): chips per scope answer status questions at once from the latest
// published data (lib/quickAnswers.ts) — shown only here, never posted to the engine or kept. The presence line says whether
// the agent watcher is on (lib/agent.ts), and each instruction shows its request's state (queued / working / done / failed).
import { useEffect, useMemo, useRef, useState, type FormEvent, type Ref } from "react";
import { Link } from "react-router-dom";
import { AgentPresenceLine } from "../../app/AgentPresence";
import { useApp } from "../../app/context";
import { useEngine } from "../../app/engine";
import { Button, Icon, StatusPill, inputCls } from "../../components";
import type { AttentionItem, InboxRow, WpMessage } from "../../data/types";
import { cleanPayload, coerceNumbers } from "../../lib/commands";
import { requestState, type RequestState } from "../../lib/agent";
import { fmtStamp } from "../../lib/format";
import { QUICK_CHIPS, quickAnswer, type ChipId, type QuickAnswer } from "../../lib/quickAnswers";
import { useLoad, usePoll, useRealtime } from "../../lib/hooks";
import { Sources, useWs } from "./common";
import { IssueRefChips, useIssueChips } from "./Issues";

export interface ChatScope { scope: "workpaper" | "engagement" | "pbc"; ref: string | null; label: string }
export type ComposeMode = "ask" | "tell" | "request" | "note";
const MODES: Array<{ id: ComposeMode; label: string; hint: string }> = [
  { id: "ask", label: "Ask", hint: "Ask a person or the agent a question. It stays open until the answer is accepted." },
  { id: "tell", label: "Tell agent", hint: "Give the agent an instruction. It is queued; the agent works it in its next session and replies here." },
  { id: "request", label: "Request from client", hint: "The agent drafts a client request. A person reviews and sends it; nothing reaches the client before that." },
  { id: "note", label: "Note", hint: "A note for the team. No one needs to act on it." },
];

/** The one inbox request a composer mode sends (fields the command's schema does not take are dropped before sending). */
export function composePayload(mode: ComposeMode, scope: ChatScope, body: string, opts: { to?: string | null; subkind?: string } = {}): { command: string; payload: Record<string, unknown> } {
  const where = { scope: scope.scope, ...(scope.ref ? { ref: scope.ref } : {}) };
  switch (mode) {
    case "ask": return { command: "wp.message", payload: { ...where, kind: "question", subkind: opts.subkind ?? "clarification", body, ...(opts.to ? { to: opts.to } : {}) } };
    case "note": return { command: "wp.message", payload: { ...where, kind: "note", body } };
    case "tell": return { command: "agent.request", payload: { ...where, body, intent: "work" } };
    case "request": return { command: "agent.request", payload: { ...where, body, intent: "client_request" } };
  }
}

const KIND_LABEL: Record<string, string> = { question: "Question", answer: "Answer", instruction: "Told the agent", agent_reply: "Agent reply", status: "Status", note: "Note", comment: "Comment" };

export function ChatPanel({ scope, onHide }: { scope: ChatScope; onHide?: () => void }) {
  const ws = useWs();
  const issueChips = useIssueChips(ws.id);                              // ADR-0034: a cited issue shows its status chip
  const { ds, me, catalogue } = useApp();
  const request = useEngine();
  const mine = useLoad(() => ds.inbox(), [ds]);
  useRealtime(ds, "inbox", mine.reload);
  usePoll(ds, mine.reload);
  const [mode, setMode] = useState<ComposeMode>("ask");
  const [body, setBody] = useState("");
  const [to, setTo] = useState<string>("");
  const [subkind, setSubkind] = useState("clarification");
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [reply, setReply] = useState("");
  const [chip, setChip] = useState<ChipId | null>(null);
  const card = useRef<HTMLElement>(null);

  const msgs = useMemo(() => ws.messages.filter((m) => m.scope === scope.scope && (scope.scope !== "workpaper" || m.ref === scope.ref))
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt)), [ws.messages, scope.scope, scope.ref]);
  const top = msgs.filter((m) => !m.parent || !msgs.some((x) => x.id === m.parent));
  const kids = (id: string) => msgs.filter((m) => m.parent === id);
  const planItem = (m: WpMessage): AttentionItem | undefined => ws.attention.find((a) => a.tab === "approval" && a.subkind === "plan" && String(a.action.payload.message ?? a.thread) === m.id);
  // CONTRACT §2: approving an agent's plan is wp.accept on the reply that carries it (the approval item names the same act).
  const approvePlan = (m: WpMessage) => (m.plan.length > 0 || planItem(m)) && m.status === "open" ? (
    <Button size="sm" kind="primary" className="mt-1" data-command={planItem(m)?.action.command ?? "wp.accept"}
      onClick={() => { const a = planItem(m); if (a?.action.command) void send(a.action.command, a.action.payload, "Approve plan"); else void send("wp.accept", { message: m.id }, "Approve plan"); }}>Approve plan</Button>
  ) : null;
  // An answered question (a person's answer or the agent's reply) waits for its asker to accept it: the engine's Action center item
  // (q:<question>, wp.accept on the latest answer) names the answer and who it waits on; the asker or a partner may accept.
  const acceptable = (q: WpMessage, a: WpMessage) => {
    if (q.kind !== "question" || q.status !== "answered" || !["answer", "agent_reply"].includes(a.kind)) return false;
    const item = ws.attention.find((x) => x.key === `q:${q.id}` && x.action.command === "wp.accept");
    const target = item ? String(item.action.payload.message ?? "") : kids(q.id).filter((x) => ["answer", "agent_reply"].includes(x.kind)).slice(-1)[0]?.id;
    const mayI = q.author === me.personName || me.role === "partner" || (item?.waitsOn.kind === "person" && item.waitsOn.name === me.personName);
    return target === a.id && mayI;
  };
  // The instant answer is recomputed from the tables as they refresh (Realtime), so it always reads the latest published rows.
  const answer = useMemo(() => chip ? quickAnswer(chip, {
    id: ws.id, syncedAt: ws.header.syncedAt, progress: ws.header.progress, wp: ws.wp, attention: ws.attention, pbc: ws.pbc, messages: ws.messages, team: ws.team,
    me: me.personName, today: new Date().toISOString().slice(0, 10),
  }, scope.ref) : null, [chip, ws, me.personName, scope.ref]);
  useEffect(() => { if (chip) card.current?.scrollIntoView?.({ block: "nearest" }); }, [chip]);
  const people = [...new Set(ws.team.map((t) => t.person))].filter((p) => p !== me.personName);

  // What this person sent here that the engine has not published yet (or refused).
  const pending = (mine.data ?? []).filter((r: InboxRow) => r.engagementId === ws.id && ["wp.message", "agent.request", "wp.answer", "wp.accept"].includes(r.command)
    && (r.status === "pending" || r.status === "running" || r.status === "refused" || r.status === "failed")
    && (r.command === "wp.answer" ? msgs.some((m) => m.id === r.payload.parent) : r.command === "wp.accept" ? msgs.some((m) => m.id === r.payload.message)
      : r.payload.scope === scope.scope && (scope.scope !== "workpaper" || r.payload.ref === scope.ref)));

  const send = async (command: string, payload: Record<string, unknown>, what: string) => {
    const spec = catalogue.get(command);
    let p = spec ? coerceNumbers(spec, cleanPayload(spec, payload)) : payload;
    // An engine whose agent.request has no `intent` field still gets the person's words; the request says what it is for.
    if (command === "agent.request" && payload.intent === "client_request" && spec && !spec.inputSchema.properties?.intent) p = { ...p, body: `Draft a client request: ${String(payload.body)}` };
    const row = await request({ command, engagementId: ws.id, payload: p, what });
    if (row) mine.reload();
    return row;
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!body.trim()) return;
    const { command, payload } = composePayload(mode, scope, body.trim(), { to: mode === "ask" ? to || null : null, subkind });
    const what = { ask: "Question", tell: "Instruction to the agent", request: "Client request for the agent to draft", note: "Note" }[mode];
    if (await send(command, payload, what)) setBody("");
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="flex items-center gap-2 border-b border-line px-3 py-2">
        <Icon name="chat" size={16} className="text-muted" />
        <h2 className="min-w-0 flex-1 truncate text-sm font-semibold">Chat · {scope.label}</h2>
        {onHide && <button type="button" onClick={onHide} aria-label="Minimize the chat" title="Minimize the chat (it stays as a strip on the right)"
          className="flex items-center gap-1 rounded px-1.5 py-1 text-xs text-muted hover:bg-sunken hover:text-ink"><Icon name="chevronRight" size={14} />Minimize</button>}
      </header>
      <AgentPresenceLine className="border-b border-line px-3 py-1.5" />
      <ol className="min-h-0 flex-1 space-y-3 overflow-y-auto px-3 py-3" aria-label={`Messages on ${scope.label}`}>
        {top.length === 0 && pending.length === 0 && <li className="py-6 text-center text-sm text-muted">No messages yet. Ask a question, tell the agent what to do, or leave a note.</li>}
        {top.map((m) => (
          <li key={m.id} className="space-y-2">
            <Bubble m={m} req={requestState(m, msgs)} chips={issueChips} engagementId={ws.id} />
            {kids(m.id).length > 0 && <ol className="ml-4 space-y-2 border-l border-line pl-3" aria-label="Replies">{kids(m.id).map((k) => (
              <li key={k.id}><Bubble m={k} chips={issueChips} engagementId={ws.id} />
                {approvePlan(k)}
                {acceptable(m, k) && (
                  <Button size="sm" kind="primary" className="mt-1" data-command="wp.accept" onClick={() => send("wp.accept", { message: k.id }, "Accept answer")}>Accept answer</Button>
                )}
              </li>
            ))}</ol>}
            <div className="flex flex-wrap gap-1.5">
              {m.kind === "question" && m.status === "open" && replyTo !== m.id && (
                <Button size="sm" onClick={() => { setReplyTo(m.id); setReply(""); }} aria-label={`Answer: ${m.body.slice(0, 60)}`}>Answer</Button>
              )}
              {approvePlan(m)}
            </div>
            {replyTo === m.id && (
              <form className="space-y-1.5" onSubmit={async (e) => { e.preventDefault(); if (!reply.trim()) return; if (await send("wp.answer", { parent: m.id, body: reply.trim() }, "Answer")) { setReplyTo(null); setReply(""); } }}>
                <label htmlFor={`ans-${m.id}`} className="sr-only">Your answer</label>
                <textarea id={`ans-${m.id}`} className={`${inputCls} min-h-16`} value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Your answer, with where you found it" />
                <div className="flex gap-1.5"><Button size="sm" kind="primary" type="submit" disabled={!reply.trim()}>Send answer</Button><Button size="sm" onClick={() => setReplyTo(null)}>Cancel</Button></div>
              </form>
            )}
          </li>
        ))}
        {pending.map((r) => (
          <li key={r.id} className={`rounded-md border border-dashed px-2.5 py-2 text-sm ${r.status === "refused" || r.status === "failed" ? "border-blocked-line bg-blocked-bg" : "border-line"}`}>
            <p className="flex items-center gap-1.5 text-[11px] text-muted"><span className="font-medium text-ink">You</span>
              <StatusPill state={r.status} label={r.status === "refused" ? "Not accepted" : r.status === "failed" ? "Failed" : "Sending"} /></p>
            <p className="mt-1 whitespace-pre-wrap">{String(r.payload.body ?? (r.command === "wp.accept" ? "Accept" : ""))}</p>
            {r.refusal && <p className="mt-1 text-xs text-blocked">{r.refusal}</p>}
          </li>
        ))}
        {answer && <li><AnswerCard cardRef={card} a={answer} onClose={() => setChip(null)} /></li>}
      </ol>
      <form onSubmit={submit} className="space-y-2 border-t border-line p-3" aria-label="Write a message">
        <div role="group" aria-label="Quick answers" className="flex flex-wrap items-center gap-1">
          <span className="text-[11px] font-medium text-muted" aria-hidden="true">Quick answers</span>
          {QUICK_CHIPS[scope.scope as keyof typeof QUICK_CHIPS]?.map((c) => (
            <button key={c.id} type="button" aria-pressed={chip === c.id} onClick={() => setChip(chip === c.id ? null : c.id)}
              className={`rounded-full border px-2 py-0.5 text-xs ${chip === c.id ? "border-brand bg-brand/10 font-semibold text-brand" : "border-dashed border-line hover:bg-sunken"}`}>{c.label}</button>
          ))}
        </div>
        <div role="group" aria-label="Message type" className="flex flex-wrap gap-1">
          {MODES.map((m) => (
            <button key={m.id} type="button" aria-pressed={mode === m.id} onClick={() => setMode(m.id)}
              className={`rounded-full border px-2 py-0.5 text-xs ${mode === m.id ? "border-brand bg-brand/10 font-semibold text-brand" : "border-line hover:bg-sunken"}`}>{m.label}</button>
          ))}
        </div>
        <p className="text-[11px] text-muted">{MODES.find((m) => m.id === mode)!.hint}</p>
        {mode === "ask" && (
          <div className="flex gap-1.5">
            <label className="sr-only" htmlFor="chat-to">Ask whom</label>
            <select id="chat-to" className={`${inputCls} py-1 text-xs`} value={to} onChange={(e) => setTo(e.target.value)}>
              <option value="">Anyone on the team</option>
              <option value="agent">The agent</option>
              {people.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
            <label className="sr-only" htmlFor="chat-kind">Kind of question</label>
            <select id="chat-kind" className={`${inputCls} py-1 text-xs`} value={subkind} onChange={(e) => setSubkind(e.target.value)}>
              <option value="clarification">Clarification</option><option value="judgment">Judgment</option><option value="help">Help</option>
            </select>
          </div>
        )}
        <label htmlFor="chat-body" className="sr-only">Message</label>
        <textarea id="chat-body" className={`${inputCls} min-h-20`} value={body} onChange={(e) => setBody(e.target.value)}
          placeholder={{ ask: "Your question…", tell: "What should the agent do?", request: "What do you need from the client, and why?", note: "Your note…" }[mode]} />
        <div className="flex justify-end"><Button kind="primary" size="sm" type="submit" disabled={!body.trim()}><Icon name="send" size={14} />Send</Button></div>
      </form>
    </div>
  );
}

/** An instant answer: computed here from the latest published data, every line a link to what it is about. */
function AnswerCard({ a, onClose, cardRef }: { a: QuickAnswer; onClose: () => void; cardRef?: Ref<HTMLElement> }) {
  return (
    <section ref={cardRef} aria-label={`Instant answer: ${a.question}`} className="rounded-md border border-brand/40 bg-brand/5 px-2.5 py-2 text-sm">
      <header className="flex items-start gap-1.5">
        <p className="min-w-0 flex-1 text-[11px] text-muted">Instant answer from the latest data · as of {a.asOf ? fmtStamp(a.asOf) : "an unknown time (not published)"}</p>
        <button type="button" onClick={onClose} aria-label="Close the instant answer" className="rounded px-1 text-muted hover:bg-sunken"><span aria-hidden="true">×</span></button>
      </header>
      <h3 className="mt-1 font-semibold">{a.question}</h3>
      <p className="mt-0.5">{a.summary}</p>
      {a.groups.map((g, gi) => (
        <div key={gi} className="mt-2">
          {g.title && <h4 className="text-xs font-semibold text-muted">{g.title}</h4>}
          <ul className="mt-1 space-y-1">
            {g.lines.map((l) => (
              <li key={l.key} className="leading-snug">
                <Link to={l.href} className="text-brand hover:underline">{l.text}</Link>
                {l.badge && <span className="ml-1.5 rounded bg-brand px-1 py-px text-[10px] font-semibold uppercase text-brand-ink">{l.badge}</span>}
                {l.detail && <span className="block text-xs text-muted">{l.detail}</span>}
                {l.also && <Link to={l.also.href} className="block text-xs text-brand hover:underline">{l.also.label}</Link>}
              </li>
            ))}
          </ul>
        </div>
      ))}
      <p className="mt-2 text-[11px] text-muted">Shown only to you; nothing was sent to the engine or the agent.</p>
    </section>
  );
}

function Bubble({ m, req, chips = null, engagementId = "" }: { m: WpMessage; req?: RequestState | null; chips?: ReturnType<typeof useIssueChips>; engagementId?: string }) {
  const { me } = useApp();
  const agent = m.author === "agent" || m.authorRole === "agent";
  const kind = KIND_LABEL[m.kind] ?? m.kind;
  return (
    <article className={`rounded-md border px-2.5 py-2 text-sm ${agent ? "border-agent-line bg-agent-bg" : "border-line bg-paper"}`} aria-label={`${kind} from ${agent ? "the agent" : m.author}`}>
      <header className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[11px] text-muted">
        {agent ? <span className="inline-flex items-center gap-1 font-medium text-agent"><span aria-hidden="true">✦</span>Agent</span>
          : <span className="font-medium text-ink">{m.author === me.personName ? "You" : m.author}</span>}
        <span>· {kind}{m.subkind ? ` · ${m.subkind}` : ""}</span>
        {m.to && <span>· to {m.to === me.personName ? "you" : m.to === "agent" ? "the agent" : m.to}</span>}
        {m.status && m.kind === "question" && <StatusPill state={m.status} label={m.status === "open" ? "Open" : m.status === "answered" ? "Answered — waiting for acceptance" : m.status === "accepted" ? "Accepted" : m.status} />}
        {req && <StatusPill state={req.pill} label={req.label} />}
        {m.scope === "pbc" && m.ref && <span className="font-mono">· PBC-{m.ref}</span>}
        <time className="ml-auto" dateTime={m.createdAt}>{fmtStamp(m.createdAt)}</time>
      </header>
      <p className="mt-1 whitespace-pre-wrap">{m.body}</p>
      {req?.reason && <p className="mt-1 text-xs text-blocked"><span className="font-medium">{req.state === "failed" ? "Why it failed" : "Why it was declined"}:</span> {req.reason}</p>}
      {m.plan.length > 0 && <ol className="mt-1 list-decimal space-y-0.5 pl-5" aria-label="The agent's plan">{m.plan.map((step, i) => <li key={i}>{step}</li>)}</ol>}
      {m.sources.length > 0 && <div className="mt-1.5 space-y-1"><Sources sources={m.sources} /><IssueRefChips sources={m.sources} engagementId={engagementId} chips={chips} /></div>}
    </article>
  );
}
